import { getExperimentTreatment } from '@ms/yammer-data/dist/domains/experiments/selectors';
import type { Store } from '@ms/yammer-data/dist/state/types';
import { ExperimentContextProvider } from '@ms/yammer-experiments-context';
import {
  useAndReportExperimentTreatment,
  useExperimentTreatmentWithoutReporting as useExperimentTreatment,
  useReportExperimentTreatmentCallback,
} from '@ms/yammer-hooks/dist/experiments';
import { useIsFeatureRolloutEnabled } from '@ms/yammer-hooks/dist/featureRollouts';
import { setQosNavigationPathname } from '@ms/yammer-scenario-telemetry';
import { PageSectionRefsProvider } from '@ms/yammer-web-components/dist/app/PageSectionRefs';
import { AnnouncerProvider } from '@ms/yammer-web-core-ui/dist/components/Announcer';
import GriffelNoopRendererProvider from '@ms/yammer-web-core-ui/dist/components/GriffelNoopRendererProvider';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import type { Persistor } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';

import AppLocationObserver from '../../../components/AppLocationObserver';
import Main from '../../../components/Main';
import type { YammerWebState } from '../../../state/types';

import RelayAppWrapper from './RelayAppWrapper';
import { getBasename } from './getBasename';
import { getErrorComponent } from './getErrorComponent';

const experimentContext = {
  useReportExperimentTreatmentCallback,
  useIsFeatureRolloutEnabled,
  useExperimentTreatment,
  useAndReportExperimentTreatment,
  getExperimentTreatment,
};

export interface RendererOptions {
  readonly store: Store<YammerWebState>;
  readonly target: HTMLElement;
  readonly persistor?: Persistor;
  readonly isFailure?: boolean;
  readonly etag?: string;
}

export type Renderer = (options: RendererOptions) => void;

const renderer: Renderer = ({ store, target, persistor, isFailure = false, etag }) => {
  const children = isFailure ? getErrorComponent(store) : <Main />;

  const basename = getBasename(store);

  const content = (
    <HelmetProvider>
      <BrowserRouter basename={basename}>
        <AppLocationObserver onLocationChange={setQosNavigationPathname} />
        <PageSectionRefsProvider>
          <AnnouncerProvider>{children}</AnnouncerProvider>
        </PageSectionRefsProvider>
      </BrowserRouter>
    </HelmetProvider>
  );

  const contentWithOptionalPersistGate = persistor ? (
    <PersistGate loading={null} persistor={persistor}>
      {content}
    </PersistGate>
  ) : (
    content
  );

  const root = createRoot(target);

  root.render(
    <GriffelNoopRendererProvider>
      <Provider store={store}>
        <ExperimentContextProvider {...experimentContext}>
          <RelayAppWrapper reduxStore={store} etag={etag}>
            {contentWithOptionalPersistGate}
          </RelayAppWrapper>
        </ExperimentContextProvider>
      </Provider>
    </GriffelNoopRendererProvider>
  );
};

export default renderer;
